import { RefundSettlementStatus } from '@/lib/enums/RefundSettlementStatus'
import type {
  RefundRequest,
  RefundRequestStatusSnapshot,
  RefundSettlementStatus as SettlementStatus,
} from '@/lib/services/refunds/refunds.types'
import { normalizeCurrencyValue as roundToCents } from '@/lib/utils'

type LoadedRefundRequest = Pick<RefundRequest, 'id' | 'settlement_status'> &
  Partial<
    Pick<
      RefundRequest,
      'amount' | 'settled_total' | 'failed_total' | 'pending_total' | 'payment_legs'
    > &
      Pick<RefundRequestStatusSnapshot, 'requested_total' | 'line_item_remaining'>
  >

function isTrackedSettlementStatus(settlementStatus: SettlementStatus): boolean {
  return [
    RefundSettlementStatus.PendingSettlement.Value,
    RefundSettlementStatus.PartiallySettled.Value,
    RefundSettlementStatus.Failed.Value,
  ].some((value) => value === settlementStatus)
}

function isActiveSettlementStatus(settlementStatus: SettlementStatus): boolean {
  return [
    RefundSettlementStatus.PendingSettlement.Value,
    RefundSettlementStatus.PartiallySettled.Value,
  ].some((value) => value === settlementStatus)
}

export function normalizeLoadedRefundRequestStatus(
  refundRequest: LoadedRefundRequest | null | undefined
): RefundRequestStatusSnapshot | null {
  if (!refundRequest || !isTrackedSettlementStatus(refundRequest?.settlement_status)) {
    return null
  }

  return {
    refund_request_id: Number(refundRequest.id),
    active_refund_request_id: Number(refundRequest.id),
    is_replaced: false,
    settlement_status: refundRequest.settlement_status,
    requested_total: refundRequest.requested_total ?? refundRequest.amount ?? '0.00',
    settled_total: refundRequest.settled_total ?? '0.00',
    failed_total: refundRequest.failed_total ?? '0.00',
    pending_total: refundRequest.pending_total ?? '0.00',
    line_item_remaining: (refundRequest.line_item_remaining ?? []).map((lineItem) => ({
      ...lineItem,
      line_item_id: Number(lineItem?.line_item_id ?? 0),
      quantity_remaining: roundToCents(lineItem?.quantity_remaining ?? 0),
      quantity_reserved: roundToCents(lineItem?.quantity_reserved ?? 0),
      amount_remaining: lineItem?.amount_remaining ?? '0.00',
      amount_reserved: lineItem?.amount_reserved ?? '0.00',
    })),
    payment_legs: (refundRequest.payment_legs ?? []).map((paymentLeg) => ({
      payment_id: Number(paymentLeg.id),
      source_payment_id: Number(paymentLeg?.source_payment_id ?? 0),
      method: paymentLeg?.method ?? null,
      provider_method: paymentLeg?.provider_method ?? null,
      destination_label: paymentLeg?.destination_label ?? paymentLeg?.method ?? null,
      amount_requested: paymentLeg?.amount_requested ?? '0.00',
      amount_settled: paymentLeg?.amount_settled ?? '0.00',
      refund_status: paymentLeg?.refund_status ?? null,
      stripe_refund_id: paymentLeg?.stripe_refund_id ?? null,
      stripe_refund_status: paymentLeg?.stripe_refund_status ?? null,
      can_confirm_cash: paymentLeg?.can_confirm_cash === true,
    })),
  }
}

export function getLoadedSettlementStatus(
  order: { refund_requests?: LoadedRefundRequest[] } | null | undefined
): RefundRequestStatusSnapshot | null {
  const refundRequests = [...(order?.refund_requests ?? [])].sort(
    (left, right) => Number(right?.id ?? 0) - Number(left?.id ?? 0)
  )

  const latestActiveRefundRequest = refundRequests.find((refundRequest) =>
    isActiveSettlementStatus(refundRequest?.settlement_status)
  )
  if (latestActiveRefundRequest) {
    return normalizeLoadedRefundRequestStatus(latestActiveRefundRequest)
  }

  // Failed requests are only actionable when they are still the latest request for this order.
  // Once a newer request exists, prefer that newer history over resurfacing the older failure.
  const latestRefundRequest = refundRequests[0] ?? null
  if (latestRefundRequest?.settlement_status === RefundSettlementStatus.Failed.Value) {
    return normalizeLoadedRefundRequestStatus(latestRefundRequest)
  }

  return null
}

export function settlementStatusSignature(
  status: Partial<RefundRequestStatusSnapshot> | null | undefined
): string {
  if (!status) {
    return ''
  }

  return JSON.stringify({
    refund_request_id: Number(status?.refund_request_id ?? 0),
    active_refund_request_id: Number(status?.active_refund_request_id ?? 0),
    settlement_status: status?.settlement_status ?? null,
    requested_total: roundToCents(status?.requested_total ?? 0),
    settled_total: roundToCents(status?.settled_total ?? 0),
    failed_total: roundToCents(status?.failed_total ?? 0),
    pending_total: roundToCents(status?.pending_total ?? 0),
    line_item_remaining: (status?.line_item_remaining ?? []).map((lineItem) => ({
      line_item_id: Number(lineItem?.line_item_id ?? 0),
      quantity_remaining: roundToCents(lineItem?.quantity_remaining ?? 0),
      quantity_reserved: roundToCents(lineItem?.quantity_reserved ?? 0),
      amount_remaining: roundToCents(lineItem?.amount_remaining ?? 0),
      amount_reserved: roundToCents(lineItem?.amount_reserved ?? 0),
    })),
    payment_legs: (status?.payment_legs ?? []).map((paymentLeg) => ({
      payment_id: Number(paymentLeg?.payment_id ?? 0),
      refund_status: paymentLeg?.refund_status ?? null,
      amount_requested: roundToCents(paymentLeg?.amount_requested ?? 0),
      amount_settled: roundToCents(paymentLeg?.amount_settled ?? 0),
      can_confirm_cash: Boolean(paymentLeg?.can_confirm_cash),
    })),
  })
}
